/**
 * TPCart — TP Butikens enda add-to-cart-implementation.
 *
 * BAKGRUND (audit 2026-09):
 * Temat hade nio separata POST-anrop till /cart/add.js och tre globala
 * submit-lyssnare i capture-fas som höll varandra i schack med
 * stopImmediatePropagation(). Vilken som vann avgjordes av i vilken ordning
 * script-taggarna råkade ligga i DOM:en.
 *
 * ARKITEKTUR:
 *   TPCart.add(items, opts)  — enda stället som POST:ar till /cart/add.js
 *   TPCart.refresh()         — hämtar auktoritativt cart-state
 *   TPCart.getState()        — senast kända cart-state (kan vara null)
 *
 * EVENTS (på document, bubblar):
 *   tp:cart:added    { items, cart, sections, source, trigger }
 *   tp:cart:updated  { cart }
 *   tp:cart:error    { error, message, source, trigger }
 *
 * Ingen annan fil får anropa /cart/add.js. Ingen annan fil får registrera
 * en global submit-lyssnare för köp. UI som ska reagera prenumererar på
 * tp:cart:added (se tp-cart-ui.js).
 */
(function () {
  'use strict';

  if (window.TPCart) return;

  var EVT_ADDED = 'tp:cart:added';
  var EVT_UPDATED = 'tp:cart:updated';
  var EVT_ERROR = 'tp:cart:error';

  var inFlight = false;
  var cartState = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  function strings() {
    return window.tpCartStrings || {};
  }

  /* ---------------------------------------------------------------
     Sektioner att rendera om i samma request som köpet.
     Sparar en extra round-trip jämfört med att hämta dem separat.
  --------------------------------------------------------------- */
  function sectionsToRender() {
    var ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.querySelector('#cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  }

  /* ---------------------------------------------------------------
     Knappens laddnings-/spärrläge. Samma beteende oavsett köpyta.
  --------------------------------------------------------------- */
  function setBusy(trigger, busy) {
    if (!trigger) return;
    var nodes = Array.isArray(trigger) ? trigger : [trigger];
    nodes.forEach(function (node) {
      if (!node || !node.classList) return;
      node.classList.toggle('tp-is-loading', busy);
      node.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) {
        node.setAttribute('aria-disabled', 'true');
        if ('disabled' in node) node.disabled = true;
      } else {
        node.removeAttribute('aria-disabled');
        if ('disabled' in node) node.disabled = false;
      }
    });
  }

  /* ---------------------------------------------------------------
     Normalisera indata. Accepterar:
       42                               -> [{id:42, quantity:1}]
       {id:42, quantity:2}              -> [{...}]
       [{id, quantity, properties}, ..] -> som det är
       FormData                         -> plockar id/quantity/properties/selling_plan
     Dubbletter av samma variant slås ihop till en rad.
  --------------------------------------------------------------- */
  function normalize(input) {
    var list = [];

    if (input instanceof FormData) {
      var item = { id: Number(input.get('id')), quantity: Number(input.get('quantity') || 1) };
      var plan = input.get('selling_plan');
      if (plan) item.selling_plan = Number(plan);
      var props = {};
      input.forEach(function (value, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = value;
      });
      if (Object.keys(props).length) item.properties = props;
      list = [item];
    } else if (Array.isArray(input)) {
      list = input.slice();
    } else if (input && typeof input === 'object') {
      list = [input];
    } else {
      list = [{ id: Number(input), quantity: 1 }];
    }

    var merged = [];
    list.forEach(function (raw) {
      if (!raw) return;
      var entry = {
        id: Number(raw.id),
        quantity: Number(raw.quantity || 1)
      };
      if (!entry.id || !isFinite(entry.id)) return;
      if (!entry.quantity || entry.quantity < 1) entry.quantity = 1;
      if (raw.selling_plan) entry.selling_plan = Number(raw.selling_plan);
      if (raw.properties && Object.keys(raw.properties).length) entry.properties = raw.properties;

      /* Rader med properties eller selling_plan är egna cart-lines och
         slås aldrig ihop. */
      var mergeable = !entry.properties && !entry.selling_plan;
      var existing = mergeable
        ? merged.find(function (m) {
            return m.id === entry.id && !m.properties && !m.selling_plan;
          })
        : null;

      if (existing) existing.quantity += entry.quantity;
      else merged.push(entry);
    });

    return merged;
  }

  function messageFrom(payload, fallback) {
    if (!payload) return fallback;
    if (typeof payload === 'string') return payload;
    if (payload.description) return payload.description;
    if (payload.message) return payload.message;
    return fallback;
  }

  /* ---------------------------------------------------------------
     Hämta auktoritativt cart-state.
  --------------------------------------------------------------- */
  function refresh() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (cart) {
          cartState = cart;
          emit(EVT_UPDATED, { cart: cart });
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* ---------------------------------------------------------------
     Enda POST:en till /cart/add.js i hela temat.
  --------------------------------------------------------------- */
  function add(input, opts) {
    opts = opts || {};
    var trigger = opts.trigger || null;

    var items = normalize(input);
    if (!items.length) {
      var e = new Error(strings().noVariant || 'Ingen variant vald.');
      emit(EVT_ERROR, { error: e, message: e.message, source: opts.source, trigger: trigger });
      return Promise.reject(e);
    }

    /* Hård spärr mot dubbla anrop: dubbelklick, submit + click-fallback,
       eller två köpytor som pekar på samma formulär. */
    if (inFlight) return Promise.resolve(null);
    inFlight = true;
    setBusy(trigger, true);

    var body = { items: items };
    var sections = sectionsToRender();
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var err = new Error(messageFrom(data, strings().genericError || 'Kunde inte lägga till i varukorgen.'));
            err.payload = data;
            err.status = res.status;
            throw err;
          }
          return data;
        });
      })
      .then(function (data) {
        var added = data.items || (data.id ? [data] : []);
        return refresh().then(function (cart) {
          emit(EVT_ADDED, {
            items: added,
            cart: cart,
            sections: data.sections || null,
            source: opts.source || 'unknown',
            trigger: trigger
          });
          return data;
        });
      })
      .catch(function (err) {
        emit(EVT_ERROR, {
          error: err,
          message: err.message || strings().genericError || 'Kunde inte lägga till i varukorgen.',
          source: opts.source || 'unknown',
          trigger: trigger
        });
        throw err;
      })
      .finally(function () {
        inFlight = false;
        setBusy(trigger, false);
      });
  }

  /* ---------------------------------------------------------------
     Formulär → TPCart.

     EN delegerad lyssnare i bubble-fas. Inga capture-lyssnare, ingen
     stopImmediatePropagation(). Ett formulär som av något skäl ska gå
     vidare till Shopify på vanligt vis markeras data-tp-cart-ignore.
  --------------------------------------------------------------- */
  function isAddForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.dataset.tpCartIgnore === 'true') return false;
    if (form.classList.contains('installment')) return false;
    if (/installment/i.test(form.id || '')) return false;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) return true;
    /* Formulär utan action men med en id-input inuti en köpyta */
    return form.dataset.tpCartForm === 'true';
  }

  function extraItemsFor(form) {
    /* Valda upsells inuti samma köpyta läggs i SAMMA request.
       Tidigare gjordes detta av ett separat script (v15-scripts part 2)
       med egen fetch och en capture-submit-lyssnare som stängde av
       resten av temat med stopImmediatePropagation(). */
    var scope =
      form.closest('[data-tp-purchase-scope]') ||
      form.closest('product-info') ||
      form.closest('#ProductInfo-' + (form.dataset.tpSection || '')) ||
      document;

    var boxes = scope.querySelectorAll(
      'input.product-upsell-checkbox:checked[data-upsell-variant], [data-tp-upsell-checkbox]:checked'
    );

    var extra = [];
    boxes.forEach(function (box) {
      var id = Number(box.dataset.upsellVariant || box.dataset.variantId || box.value);
      if (!id) return;
      extra.push({ id: id, quantity: Number(box.dataset.quantity || 1) });
    });
    return extra;
  }

  /* Exponeras så att sticky buy kan visa rätt totalsumma. */
  function selectedUpsellTotal(form) {
    if (!form) return 0;
    var scope = form.closest('product-info') || document;
    var total = 0;
    scope
      .querySelectorAll('input.product-upsell-checkbox:checked[data-upsell-variant]')
      .forEach(function (box) {
        var card = box.closest('.product-upsell-card');
        var select = card ? card.querySelector('[data-upsell-select]') : null;
        var option = select && select.options ? select.options[select.selectedIndex] : null;
        total += Number((option && option.dataset.price) || box.dataset.upsellPrice || 0);
      });
    return total;
  }

  function submitForm(form, submitter, source) {
    var fd = new FormData(form);
    if (submitter && submitter.name && submitter.name !== 'add') {
      fd.append(submitter.name, submitter.value);
    }
    var items = normalize(fd).concat(extraItemsFor(form));
    var trigger = submitter || form.querySelector('[type="submit"]');
    return add(items, { source: source || 'form', trigger: trigger, form: form });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isAddForm(form)) return;

    var submitter = event.submitter || null;
    /* "Köp nu"/dynamic checkout-knappar äger sin egen submit. */
    if (submitter && submitter.name === 'checkout') return;

    event.preventDefault();
    submitForm(form, submitter, form.dataset.tpCartSource);
  });

  /* ---------------------------------------------------------------
     <product-form> — EN definition.

     Fanns tidigare i två filer (product-form.js och cart-drawer.js) med
     var sin submit-hanterare. product-info.js anropar toggleSubmitButton()
     och handleErrorMessage() på elementet, så API:et behålls.
  --------------------------------------------------------------- */
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class TPProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;
          var idInput = this.form.querySelector('[name="id"]');
          if (idInput) idInput.disabled = false;
          this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        }

        toggleSubmitButton(disable, text) {
          var button = this.querySelector('[type="submit"]');
          if (!button) return;
          if (disable) {
            button.setAttribute('disabled', 'disabled');
            if (text) {
              var label = button.querySelector('span') || button;
              label.textContent = text;
            }
          } else {
            button.removeAttribute('disabled');
          }
        }

        handleErrorMessage(message) {
          if (!this.errorWrapper) return;
          this.errorWrapper.hidden = !message;
          var target = this.errorWrapper.querySelector('.product-form__error-message') || this.errorWrapper;
          target.textContent = message || '';
        }
      }
    );
  }

  /* Fel från TPCart visas i produktformulärets felruta när det finns en. */
  document.addEventListener(EVT_ERROR, function (event) {
    var trigger = event.detail && event.detail.trigger;
    var host = trigger && trigger.closest ? trigger.closest('product-form') : null;
    if (host && typeof host.handleErrorMessage === 'function') {
      host.handleErrorMessage(event.detail.message);
    }
  });

  document.addEventListener(EVT_ADDED, function (event) {
    var trigger = event.detail && event.detail.trigger;
    var host = trigger && trigger.closest ? trigger.closest('product-form') : null;
    if (host && typeof host.handleErrorMessage === 'function') {
      host.handleErrorMessage('');
    }
  });

  window.TPCart = {
    add: add,
    refresh: refresh,
    submitForm: submitForm,
    selectedUpsellTotal: selectedUpsellTotal,
    getState: function () {
      return cartState;
    },
    get isBusy() {
      return inFlight;
    },
    EVENTS: { ADDED: EVT_ADDED, UPDATED: EVT_UPDATED, ERROR: EVT_ERROR }
  };
})();
